import { ContentType } from "./dataTypes/ContentType";
import { FieldDescriptor } from "./FieldDescriptor";
import { Field, MediaField } from "./graphFieldsExtractor";

/**
 * Advanced (presentation / runtime) field catalog — roadmap Phase 4 (§2.A/§2.B/§2.C).
 *
 * The node-form's Core fields come from two paths (the backend unified schema
 * for slopify types, the frontend legacy extractor for the rest). Advanced
 * presentation columns (font_size, color, timers, volume, looping, region
 * sizes, …) live in NEITHER source — they're non-translatable content columns
 * that the slopify schema and the PLD map don't carry. Rather than teach both
 * paths about them, we inject them from this single frontend catalog AFTER the
 * Core fields are extracted (`buildAdvancedFields` in NodeForm), so one table
 * covers every type regardless of which Core path produced its fields.
 *
 * Scalar advanced fields carry `tier: "advanced"` and render inside the
 * collapsible "Advanced" section. Advanced MEDIA fields (audio_success/fail,
 * source_video_tuto, …) render in the media strip alongside Core media — their
 * tier is documentation/ordering only (§0.4), so they're plain MediaFields.
 *
 * Numeric bounds / display modes come verbatim from Appendix C (the VR editor
 * prefab slider ranges). `displayMode: "percent"` is DISPLAY-ONLY — the raw
 * float is stored (Appendix C load-bearing note); DynamicField mirrors the %.
 */

/** Base content columns whose VR write-target is the base `contents` row
 * (§0.2 / §2.A / §2.D pt 9). On save these route to `PUT /contents/:id`, never
 * to the per-type endpoint (graphLegacy.updateContentType). */
export const BASE_CONTENT_FIELD_KEYS = ["time_code", "is_pause_scene_on_play", "be_scored"] as const;

type NumberSpec = {
  kind: "number";
  key: string;
  min?: number;
  max?: number;
  displayMode?: "decimal" | "integer" | "percent";
};
type BooleanSpec = { kind: "boolean"; key: string; labelKey?: string };
type ColorSpec = { kind: "color"; key: string };
type MediaSpec = { kind: "media"; key: string; mediaType: MediaField["mediaType"] };
type AdvancedSpec = NumberSpec | BooleanSpec | ColorSpec | MediaSpec;

const num = (
  key: string,
  min: number | undefined,
  max: number | undefined,
  displayMode: NumberSpec["displayMode"],
): NumberSpec => ({ kind: "number", key, min, max, displayMode });
const bool = (key: string, labelKey?: string): BooleanSpec => ({ kind: "boolean", key, labelKey });
const color = (key: string): ColorSpec => ({ kind: "color", key });
const media = (key: string, mediaType: MediaField["mediaType"]): MediaSpec => ({
  kind: "media",
  key,
  mediaType,
});

// Base runtime toggles VR exposes on every prefab (§2.D pt 9). `time_code` has
// no VR slider bounds (its own SetTimeCode widget) → seconds, min 0. Applied to
// every interaction type below (the avatar track is handled separately, §4).
const BASE_UNIVERSAL: AdvancedSpec[] = [
  num("time_code", 0, undefined, "decimal"),
  bool("is_pause_scene_on_play"),
];

/** Per-type advanced specs (excluding the universal base toggles above). */
const PER_TYPE: Record<string, AdvancedSpec[]> = {
  "content-text": [
    num("font_size", 10, 60, "decimal"),
    color("color"),
    bool("has_background"),
    num("timer", 0, 50, "decimal"),
    bool("does_disable_self_from_timer"),
  ],
  "content-onboarding": [
    num("font_size", 10, 100, "decimal"),
    num("timer", 0, 50, "decimal"),
    bool("does_disable_self_from_timer"),
  ],
  "content-danger-catch": [
    num("font_size", 10, 80, "decimal"),
    num("catch_time", 1, 10, "integer"),
    bool("is_hidden_at_start"),
    bool("be_scored"),
    media("audio_success", "audio"),
    media("audio_fail", "audio"),
  ],
  "content-photo-catch": [
    num("font_size", 10, 80, "decimal"),
    num("catch_time", 1, 10, "integer"),
    bool("is_hidden_at_start"),
    bool("be_scored"),
  ],
  "content-thumbnail": [num("font_size", 10, 75, "decimal"), color("color")],
  "content-hotspot-scene-image": [
    color("color"),
    num("font_size", 10, 80, "decimal"),
    bool("has_activate_animation"),
    bool("is_hidden_at_start"),
  ],
  "content-hotspot-scene-video": [
    color("color"),
    num("font_size", 10, 80, "decimal"),
    bool("has_activate_animation"),
    bool("is_hidden_at_start"),
  ],
  "content-video-2-d": [
    color("color"),
    // VR labels the video loop toggle differently from the audio/scene loop
    // (loc_videoLoop "Video loop" vs loc_loop "Is looping") — per-type label.
    bool("is_looping", "is_looping_video"),
    bool("has_video_controller"),
    num("sound_volume", 0, 1, "percent"),
  ],
  "content-audio": [
    bool("is_looping"),
    bool("is_spatialized"),
    num("sound_distance", 0, 1, "decimal"),
    num("sound_volume", 0, 1, "percent"),
  ],
  "content-avatar-audio-player": [
    bool("is_looping"),
    bool("is_spatialized"),
    num("sound_distance", 0, 1, "decimal"),
    num("sound_volume", 0, 1, "percent"),
  ],
  "content-3-d-viewer": [num("height", 0, 1, "decimal"), bool("is_self_rotating")],
  "content-3d-viewer": [num("height", 0, 1, "decimal"), bool("is_self_rotating")],
  "content-pdf-viewer": [num("page_to_start", 1, undefined, "integer")],
  "content-fire-extinguisher": [bool("be_scored"), media("source_video_tuto", "video")],
  "content-zone-sort": [
    num("timer", 0, 60, "decimal"),
    bool("be_scored"),
    media("source_video_tuto", "video"),
  ],
  "content-zone-detect": [num("timer", 0, 60, "decimal")],
  "content-clean": [media("source_video_tuto", "video")],
  "content-element-catch": [
    num("game_duration", 1, 60, "integer"),
    media("source_video_tuto", "video"),
  ],
  "content-random-quizz": [num("nb_questions", 1, 20, "integer")],
  "content-speech-to-check": [
    num("respond_timer", 1, 30, "decimal"),
    num("time_begin", 0, 30, "integer"),
  ],
  "content-blur": [
    num("blur_region_width", 0.01, 7, "decimal"),
    num("blur_region_height", 0.01, 7, "decimal"),
  ],
  "content-fire": [num("fire_size", 0.01, 7, "percent")],
  "content-dust": [color("color")],
  "content-highlight": [num("content_size", 0.5, 2, "decimal")],
  "content-tuto": [bool("has_audio")],
  "content-button-1": [color("color")],
  "content-button-reload": [color("color")],
  // scene sound_volume/is_looping are conditional (§2.D pt 3) — enabled only for
  // video360 scenes. They live here so they render in the Advanced section; the
  // conditional disable + tooltip is applied in NodeForm.
  "content-scene": [bool("is_looping"), num("sound_volume", 0, 1, "percent")],
  // WON'T DO (kept as documentation of the deliberate omissions):
  //  - content-scoring-general.enable_redo (§2.D pt 1)
  //  - content-scene.time_begin / time_end (§2.D pt 2)
  //  - content-vr-locator.activate_start_panel — ON HOLD (Appendix A pt 1)
  //  - depth / scale / positions everywhere (§2.D pt 10)
};

// The avatar track (§4) is scoped against the AI service, not the VR editor —
// it gets no VR-parity advanced fields here.
const AVATAR_TRACK = new Set([
  "content-avatar-ai",
  "content-avatar-ai-persona",
  "content-avatar-ai-session-debrief",
]);

const makeDescriptor = (spec: AdvancedSpec): FieldDescriptor => {
  const base = {
    key: spec.key,
    description: null,
    required: false,
    pldKey: null,
    isTranslatable: false,
    tier: "advanced" as const,
  };
  switch (spec.kind) {
    case "number":
      return {
        ...base,
        widget: "number",
        jsonSchema: {
          type: spec.displayMode === "integer" ? "integer" : "number",
          ...(spec.min !== undefined ? { minimum: spec.min } : {}),
          ...(spec.max !== undefined ? { maximum: spec.max } : {}),
        },
        displayMode: spec.displayMode ?? "decimal",
      };
    case "boolean":
      return {
        ...base,
        widget: "boolean",
        jsonSchema: { type: "boolean" },
        ...(spec.labelKey ? { labelKey: spec.labelKey } : {}),
      };
    case "color":
      return { ...base, widget: "color", jsonSchema: { type: "string" } };
    case "media":
      return { ...base, widget: "media", jsonSchema: {}, tier: undefined };
  }
};

const BASE_CONTENT_FIELD_KEY_SET = new Set<string>(BASE_CONTENT_FIELD_KEYS);

const specToField = (
  spec: AdvancedSpec,
  contentObject: Record<string, unknown>,
  baseContentObject?: Record<string, unknown>,
): Field => {
  const descriptor = makeDescriptor(spec);
  // For columns that are authoritative on the base contents row, prefer that
  // value over the potentially-stale per-type copy.
  const currentValue =
    BASE_CONTENT_FIELD_KEY_SET.has(spec.key) && baseContentObject != null
      ? baseContentObject[spec.key] ?? contentObject?.[spec.key]
      : contentObject?.[spec.key];
  switch (spec.kind) {
    case "media":
      return {
        type: "media",
        mediaType: spec.mediaType,
        fieldName: spec.key,
        currentValue: currentValue as MediaField["currentValue"],
        descriptor,
      };
    case "boolean":
      return { type: "boolean", fieldName: spec.key, currentValue: !!currentValue, descriptor };
    case "number":
      return {
        type: "number",
        fieldName: spec.key,
        currentValue: currentValue as number | string | undefined,
        descriptor,
      };
    case "color":
      return {
        type: "color",
        fieldName: spec.key,
        currentValue: currentValue as string | undefined,
        descriptor,
      };
  }
};

/**
 * Build the advanced Field[] for a content node, reading current values off the
 * content object. Excludes the avatar track. Callers dedup against the Core
 * fields already extracted (a spec whose key a Core field already covers is
 * dropped so we never render a column twice).
 */
export const buildAdvancedFields = (
  contentType: ContentType,
  baseContentObject?: Record<string, unknown>,
): Field[] => {
  const modelName = contentType.modelName;
  if (AVATAR_TRACK.has(modelName)) return [];
  const specs = [...BASE_UNIVERSAL, ...(PER_TYPE[modelName] ?? [])];
  const contentObject = (contentType.object as unknown as Record<string, unknown>) ?? {};
  // Guard against accidental duplicate keys across the universal + per-type sets.
  const seen = new Set<string>();
  const fields: Field[] = [];
  for (const spec of specs) {
    if (seen.has(spec.key)) continue;
    seen.add(spec.key);
    fields.push(specToField(spec, contentObject, baseContentObject));
  }
  return fields;
};

export const isAdvancedField = (field: Field): boolean => field.descriptor?.tier === "advanced";
